import type { MiniAction } from '@otto-ec/tracking-bct';
import { log } from '@shared/log.browser.ts';
import defaultTrackingGateway from '@shared/tracking/Tracking.browser';
import type { TrackingGateway } from '@shared/tracking/Tracking.browser';
import type { FrontendTrackingLabels, KnownTrackingLabels } from '@shared/tracking/TrackingLabels';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import { listFeaturesLabels } from '@tilelist/domain/black-friday-slider/BlackFridaySliderTracking.ts';
import type { ProductItem, TileItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import {
    featureIdFromLocalListPosition,
    featuresFromTileListItems,
} from '@tilelist/domain/tracking/FeatureTracking.ts';
import { comboProductClickLabels, productClickLabels } from '@tilelist/services/tracking/ProductClickLabels.browser.ts';

export class TrackingService implements TrackingPort {
    constructor(private readonly gateway: TrackingGateway = defaultTrackingGateway) {}

    async getPageViewTrackingSfid(): Promise<string> {
        return this.gateway.getPageMergeId();
    }

    async replaceContext(contextId: string, url: string): Promise<void> {
        log.debug(`replaceContext -- contextId: ${contextId} -- url: ${url}`);
        await this.gateway.replaceContext(contextId, url);
    }

    trackTileVisibility(item: TileItem): void {
        const featureId = featureIdFromLocalListPosition(item.localListPosition);
        const miniAction: MiniAction = { featureId, action: 'scroll', status: 'visible' };

        log.debug(`trackTileVisibility -- miniAction ${JSON.stringify(miniAction)}`);
        this.gateway.submitMiniActions([miniAction]);
    }

    trackClick(event: Event, productItem: ProductItem, rule: string, entryRule: string): void {
        const trackingData = productClickLabels(event, productItem, rule, entryRule);
        if (!trackingData) return;

        log.debug(`trackClick ${JSON.stringify(trackingData)}`);
        this.gateway.trackOnNextPageImpression(trackingData);
    }

    trackComboProductClick(event: Event, position: number): void {
        const trackingData = comboProductClickLabels(event, position);

        log.debug(`trackComboProductClick ${JSON.stringify(trackingData)}`);
        this.gateway.trackOnNextPageImpression(trackingData);
    }

    trackPageView(pageView: TileListPageLayout, frontendTracking: FrontendTrackingLabels): void {
        const baseTrackingLabels = {
            ...frontendTracking,
            ...listFeaturesLabels(pageView.tileListItems),
            ...pageView.tracking,
        };

        const features = featuresFromTileListItems(pageView.tileListItems);

        log.debug(`trackOnPageView ${JSON.stringify(baseTrackingLabels)} -- ${JSON.stringify(features)}`);
        this.gateway.addToPageImpression(baseTrackingLabels || {}, features || []);
    }

    trackPagination() {
        log.debug(`trackPagination -- san_Interaction: paging_bottom`);
        this.gateway.addToPageImpression({ san_Interaction: 'paging_bottom' });
    }

    trackUrl(url: string): void {
        log.debug(`trackUrl ${url}`);
        this.gateway.addToPageImpression({
            ot_VisibleUrl: decodeURI(url),
        });
    }

    trackPageCluster(pageCluster: string) {
        log.debug(`trackPageCluster ${pageCluster}`);
        this.gateway.addToPageImpression({ ot_PageCluster: pageCluster });
    }

    trackComparisonActivity(activity: NonNullable<KnownTrackingLabels['san_ComparisonActivity']>) {
        log.debug(`trackComparisonActivity -- activity: ${activity}`);
        this.gateway.submitEvent({ san_ComparisonActivity: activity });
    }

    trackInteraction(interaction: KnownTrackingLabels['san_Interaction']) {
        log.debug(`trackInteraction -- san_Interaction: ${interaction}`);
        this.gateway.submitEvent({ san_Interaction: interaction });
    }

    trackProductImageChange(imagePosition: number) {
        const flexLabel = `image_${imagePosition}`;
        log.debug(
            `trackProductImageChange -- san_Interaction: product_image_change -- san_ReptileExperimentFlexLabel: ${flexLabel}`,
        );
        this.gateway.submitEvent({
            san_Interaction: 'product_image_change',
            san_ReptileExperimentFlexLabel: flexLabel,
        });
    }

    trackListLayer(layer: NonNullable<KnownTrackingLabels['san_ListLayer']>) {
        log.debug(`trackListLayer -- san_ListLayer: ${layer}`);
        this.gateway.submitEvent({ san_ListLayer: layer });
    }
}
